import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate, formatTime, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  Dialog,
  Overlay,
  Pill,
  Sheet,
  Skeleton,
  TextField,
  useToast,
} from '../../ds/components';
import { lockBodyScroll } from '../../ds/components/Sheet';
import { useEscapeKey, useFocusTrap, useIsMobile } from '../../ds/hooks';
import { ApiError, api, errorMessage } from '../../lib/api';
import { sessionQueryKey, useSession } from '../../lib/session';
import { PASSWORD_MISMATCH, PASSWORD_POLICY_HINT, type RecoveryCodesResponse } from '../auth/auth';
import { RecoveryCodes } from '../auth/RecoveryCodes';
import { TwoFactorSetupPanel } from '../auth/TwoFactorSetupPanel';
import { SettingsPageHeader } from './SettingsFrame';
import styles from './SecuritySettingsPage.module.css';

interface SessionItem {
  id: string;
  device: string | null;
  browser: string | null;
  location: string | null;
  createdAt: string;
  lastActivityAt: string;
  current: boolean;
}

interface SecuritySettings {
  passwordChangedAt: string | null;
  passwordAgeDays: number | null;
  twoFactor: { enabled: boolean; enabledAt: string | null };
  sessions: SessionItem[];
}

const securityKey = ['settings', 'security'] as const;

function useSecurity() {
  return useQuery({ queryKey: securityKey, queryFn: () => api<SecuritySettings>('/settings/security') });
}

/** "heute 08:02" for today, otherwise "12.09.2026". */
function relativeDay(iso: string, now = new Date()): string {
  return toBerlinDateKey(iso) === toBerlinDateKey(now) ? `heute ${formatTime(iso)}` : formatDate(iso);
}

function sessionName(session: SessionItem): string {
  const parts = [session.device, session.browser, session.location].filter(
    (part): part is string => typeof part === 'string' && part.length > 0,
  );
  return parts.length > 0 ? parts.join(' · ') : 'Unbekanntes Gerät';
}

/** Einstellungen 16 "Passwort ändern": current + new + repeat, policy from §9.1. */
function PasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const change = useMutation({
    mutationFn: () =>
      api<void>('/auth/password', { method: 'POST', body: { currentPassword: current, newPassword: next } }),
    onSuccess: async () => {
      toast({ message: 'Passwort geändert. Alle anderen Sitzungen wurden beendet.' });
      await queryClient.invalidateQueries({ queryKey: securityKey });
      reset();
      onClose();
    },
  });

  function reset() {
    setCurrent('');
    setNext('');
    setRepeat('');
    setMismatch(false);
    change.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (next !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    change.mutate();
  }

  const fields = change.isError && change.error instanceof ApiError ? change.error.fields : {};
  const formId = 'password-change-form';

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      eyebrow="Sicherheit"
      title="Passwort ändern"
      width={560}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={change.isPending}>
            Passwort ändern
          </Button>
        </>
      }
      footerNote="Alle anderen Sitzungen werden beendet."
    >
      <form id={formId} className={styles.form} onSubmit={submit} noValidate>
        {change.isError && Object.keys(fields).length === 0 ? (
          <Banner tone="danger">{errorMessage(change.error)}</Banner>
        ) : null}
        <TextField
          label="Aktuelles Passwort"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          error={fields['currentPassword']}
        />
        <TextField
          label="Neues Passwort"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          hint={PASSWORD_POLICY_HINT}
          error={fields['newPassword']}
        />
        <TextField
          label="Neues Passwort wiederholen"
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          error={mismatch ? PASSWORD_MISMATCH : undefined}
        />
      </form>
    </Dialog>
  );
}

/** Regenerates the ten recovery codes after a password check (Einstellungen 16). */
function RecoveryCodesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [saved, setSaved] = useState(false);
  const regenerate = useMutation({
    mutationFn: () =>
      api<RecoveryCodesResponse>('/auth/2fa/recovery-codes', { method: 'POST', body: { password } }),
  });
  const codes = regenerate.data?.recoveryCodes ?? null;
  const fields = regenerate.isError && regenerate.error instanceof ApiError ? regenerate.error.fields : {};
  const formId = 'recovery-codes-form';

  function close() {
    setPassword('');
    setSaved(false);
    regenerate.reset();
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      closeOnScrim={codes === null}
      eyebrow="Sicherheit"
      title={codes ? 'Wiederherstellungscodes' : 'Wiederherstellungscodes erneuern'}
      subtitle={
        codes
          ? 'Zehn neue Codes, jeder gilt einmal. Bewahren Sie sie außerhalb des Handys auf.'
          : 'Die bisherigen zehn Codes verlieren ihre Gültigkeit. Bestätigen Sie mit Ihrem Passwort.'
      }
      width={560}
      footer={
        codes ? (
          <Button disabled={!saved} onClick={close}>
            Fertig
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close}>
              Abbrechen
            </Button>
            <Button type="submit" form={formId} loading={regenerate.isPending} disabled={!password}>
              Neue Codes erzeugen
            </Button>
          </>
        )
      }
    >
      {codes ? (
        <div className={styles.form}>
          <RecoveryCodes codes={codes} />
          <Checkbox
            label="Ich habe die Codes kopiert oder gespeichert."
            checked={saved}
            onChange={(event) => setSaved(event.target.checked)}
          />
        </div>
      ) : (
        <form
          id={formId}
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            regenerate.mutate();
          }}
          noValidate
        >
          {regenerate.isError && Object.keys(fields).length === 0 ? (
            <Banner tone="danger">{errorMessage(regenerate.error)}</Banner>
          ) : null}
          <TextField
            label="Passwort"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={fields['password']}
          />
        </form>
      )}
    </Dialog>
  );
}

/** Einstellungen 17 as a 680 px panel over the page (a sheet on mobile). */
function TwoFactorDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const mobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const desktop = open && !mobile;
  useEscapeKey(onClose, desktop);
  useFocusTrap(panelRef, desktop, { initialFocus: 'first' });
  useEffect(() => (desktop ? lockBodyScroll() : undefined), [desktop]);

  async function done() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: securityKey }),
      queryClient.invalidateQueries({ queryKey: sessionQueryKey }),
    ]);
    onClose();
  }

  if (!open) return null;
  const panel = (
    <TwoFactorSetupPanel
      variant="settings"
      cancelLabel="Abbrechen"
      onCancel={onClose}
      onDone={() => void done()}
    />
  );
  if (mobile) {
    return (
      <Sheet open={open} onClose={onClose} label="Zwei-Faktor einrichten" closeOnScrim={false}>
        {panel}
      </Sheet>
    );
  }
  return createPortal(
    <Overlay top={60}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Zwei-Faktor einrichten"
        tabIndex={-1}
        className={styles.setupDialog}
      >
        {panel}
      </div>
    </Overlay>,
    document.body,
  );
}

function SessionsList({
  sessions,
  onEnd,
  ending,
}: {
  sessions: SessionItem[];
  onEnd: (id: string) => void;
  ending: string | null;
}) {
  return (
    <div className={styles.sessions}>
      {sessions.map((session) => (
        <div key={session.id} className={styles.sessionRow}>
          <span
            className={clsx(styles.sessionDot, session.current && styles.sessionDotCurrent)}
            aria-hidden="true"
          />
          <span className={styles.sessionText}>
            <span className={clsx(styles.sessionName, session.current && styles.sessionNameCurrent)}>
              {sessionName(session)}
            </span>
            <span className={styles.sessionSub}>
              {session.current
                ? `Diese Sitzung · aktiv seit ${relativeDay(session.createdAt)}`
                : `Zuletzt aktiv ${relativeDay(session.lastActivityAt)}`}
            </span>
          </span>
          {session.current ? (
            <Pill variant="success">Aktuell</Pill>
          ) : (
            <Button
              variant="secondary"
              size="small"
              loading={ending === session.id}
              onClick={() => onEnd(session.id)}
            >
              Abmelden
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Text of the 2FA card. With `twoFactorRequired = false` (`TWO_FACTOR_MODE=optional`, D-338) the login does not ask
 * for the code: TOTP stays available as an opt-in, the card shows „Optional" instead of the warning.
 */
function twoFactorText(enabled: boolean, enabledAt: string | null, required: boolean): string {
  const since = enabledAt ? formatDate(enabledAt) : '—';
  if (enabled) {
    return required
      ? `Aktiv seit ${since}. Jeder Login braucht den Code aus der Authenticator-App.`
      : `Aktiv seit ${since}. Die Anmeldung fragt den Code derzeit nicht ab.`;
  }
  return required
    ? 'Zwei-Faktor ist für alle internen Nutzer Pflicht. Richten Sie sie jetzt ein.'
    : 'Optional — Sie können die Zwei-Faktor-Anmeldung hier einrichten.';
}

/** `/settings/security` — Einstellungen 16 (desktop) and M8 (mobile). */
export function SecuritySettingsPage() {
  const mobile = useIsMobile();
  const required = useSession().twoFactorRequired;
  const queryClient = useQueryClient();
  const security = useSecurity();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [codesOpen, setCodesOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [ending, setEnding] = useState<string | null>(null);

  const endSession = useMutation({
    mutationFn: (id: string) => api<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
    onMutate: (id) => setEnding(id),
    onSettled: async () => {
      setEnding(null);
      await queryClient.invalidateQueries({ queryKey: securityKey });
    },
  });
  const endOthers = useMutation({
    mutationFn: () => api<void>('/auth/sessions?others=true', { method: 'DELETE' }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: securityKey });
    },
  });

  const data = security.data;
  const twoFactorOn = data?.twoFactor.enabled ?? true;
  const warning = !twoFactorOn && required;
  const offPill = required ? (
    <Pill variant="warning">Nicht aktiv</Pill>
  ) : (
    <Pill variant="neutral">Optional</Pill>
  );
  const passwordText = data
    ? data.passwordChangedAt && data.passwordAgeDays !== null
      ? `Zuletzt geändert am ${formatDate(data.passwordChangedAt)} · ${data.passwordAgeDays} ${
          data.passwordAgeDays === 1 ? 'Tag' : 'Tage'
        }`
      : 'Noch nie geändert'
    : null;
  const mutationError = endSession.error ?? endOthers.error;

  const dialogs = (
    <>
      <PasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      <TwoFactorDialog open={setupOpen} onClose={() => setSetupOpen(false)} />
      <RecoveryCodesDialog open={codesOpen} onClose={() => setCodesOpen(false)} />
    </>
  );

  if (mobile) {
    return (
      <div className={styles.mobile}>
        {security.isError ? <Banner tone="danger">{errorMessage(security.error)}</Banner> : null}
        {mutationError ? <Banner tone="danger">{errorMessage(mutationError)}</Banner> : null}
        <div className={clsx(styles.mobileCard, warning && styles.warningFrame)}>
          <div className={styles.mobileCardHead}>
            <span className={styles.mobileCardTitle}>Zwei-Faktor-Anmeldung</span>
            {data ? (
              twoFactorOn ? (
                <Pill variant="success" dot>
                  Aktiv
                </Pill>
              ) : (
                offPill
              )
            ) : (
              <Skeleton width={60} />
            )}
          </div>
          <span className={styles.mobileCardText}>
            {data ? twoFactorText(twoFactorOn, data.twoFactor.enabledAt, required) : ''}
          </span>
          {twoFactorOn ? (
            <Button variant="secondary" onClick={() => setCodesOpen(true)}>
              Wiederherstellungscodes erneuern
            </Button>
          ) : (
            <Button onClick={() => setSetupOpen(true)}>Einrichten</Button>
          )}
        </div>
        <div className={styles.mobileList}>
          <button type="button" className={styles.mobileItem} onClick={() => setPasswordOpen(true)}>
            <span className={styles.mobileItemText}>
              <span className={styles.mobileItemName}>Passwort ändern</span>
              <span className={styles.mobileItemSub}>
                {data
                  ? data.passwordChangedAt
                    ? `zuletzt ${formatDate(data.passwordChangedAt)}`
                    : 'noch nie geändert'
                  : ''}
              </span>
            </span>
            <span className={styles.chevron} aria-hidden="true">
              ›
            </span>
          </button>
          <button type="button" className={styles.mobileItem} onClick={() => setSessionsOpen(true)}>
            <span className={styles.mobileItemText}>
              <span className={styles.mobileItemName}>Aktive Sitzungen</span>
              <span className={styles.mobileItemSub}>
                {data ? `${data.sessions.length} ${data.sessions.length === 1 ? 'Gerät' : 'Geräte'}` : ''}
              </span>
            </span>
            <span className={styles.chevron} aria-hidden="true">
              ›
            </span>
          </button>
        </div>
        {!twoFactorOn ? (
          <div className={styles.hintBox}>
            Nach Aktivierung erhalten Sie zehn Wiederherstellungscodes. Bewahren Sie sie außerhalb des Handys
            auf.
          </div>
        ) : null}
        <Sheet
          open={sessionsOpen}
          onClose={() => setSessionsOpen(false)}
          title="Aktive Sitzungen"
          footer={
            <Button
              variant="secondary"
              size="small"
              loading={endOthers.isPending}
              onClick={() => endOthers.mutate()}
            >
              Alle anderen abmelden
            </Button>
          }
        >
          {data ? (
            <SessionsList sessions={data.sessions} onEnd={(id) => endSession.mutate(id)} ending={ending} />
          ) : null}
        </Sheet>
        {dialogs}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <SettingsPageHeader eyebrow="Meine Einstellungen" title="Sicherheit" />
      {security.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void security.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(security.error)}
        </Banner>
      ) : null}
      {mutationError ? <Banner tone="danger">{errorMessage(mutationError)}</Banner> : null}
      <div className={styles.grid}>
        <Card className={styles.card}>
          <span className={styles.cardTitle}>Passwort</span>
          <span className={styles.cardText}>{passwordText ?? <Skeleton width={220} />}</span>
          <Button
            variant="secondary"
            className={styles.cardAction}
            disabled={!data}
            onClick={() => setPasswordOpen(true)}
          >
            Passwort ändern
          </Button>
        </Card>
        <Card className={clsx(styles.card, data && warning && styles.warningFrame)}>
          <span className={styles.cardTitleRow}>
            <span className={styles.cardTitle}>Zwei-Faktor-Anmeldung</span>
            {data ? (
              twoFactorOn ? (
                <Pill variant="success" dot>
                  Aktiv
                </Pill>
              ) : (
                offPill
              )
            ) : null}
          </span>
          <span className={styles.cardText}>
            {data ? twoFactorText(twoFactorOn, data.twoFactor.enabledAt, required) : <Skeleton width={260} />}
          </span>
          {data ? (
            twoFactorOn ? (
              <Button variant="secondary" className={styles.cardAction} onClick={() => setCodesOpen(true)}>
                Wiederherstellungscodes erneuern
              </Button>
            ) : (
              <Button className={styles.cardAction} onClick={() => setSetupOpen(true)}>
                Einrichten
              </Button>
            )
          ) : null}
        </Card>
      </div>
      <Card flush className={styles.sessionsCard}>
        <div className={styles.sessionsHead}>
          <span className={styles.cardTitle}>Aktive Sitzungen</span>
          <Button
            variant="ghost"
            size="small"
            className={styles.sessionsAction}
            loading={endOthers.isPending}
            disabled={!data || data.sessions.length < 2}
            onClick={() => endOthers.mutate()}
          >
            Alle anderen abmelden
          </Button>
        </div>
        {data ? (
          <SessionsList sessions={data.sessions} onEnd={(id) => endSession.mutate(id)} ending={ending} />
        ) : (
          <div className={styles.sessionsSkeleton} aria-busy="true">
            <Skeleton width="40%" />
            <Skeleton width="55%" />
          </div>
        )}
      </Card>
      {dialogs}
    </div>
  );
}
